import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Settings, User, Building2, CreditCard, Globe, Clock } from 'lucide-react';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';

interface LocalSettings {
  invoice_due_days: number;
  low_stock_threshold: number;
  sos_primary_number: string;
  business_hours_start: string;
  business_hours_end: string;
}

export function GlobalSettingsPage() {
  const { config, loading: configLoading, refreshConfig } = useBusinessConfig();
  const [loading, setLoading] = useState(false);
  const [localSettings, setLocalSettings] = useState<LocalSettings>({
    invoice_due_days: 14,
    low_stock_threshold: 5,
    sos_primary_number: '000',
    business_hours_start: '08:00',
    business_hours_end: '18:00',
  });

  // Form state for business_config fields
  const [formData, setFormData] = useState({
    biz_name: '',
    biz_type: 'detailing',
    biz_mode: 'fixed',
    owner_name: '',
    contact_email: '',
    contact_phone: '',
    abn: '',
    gst_registered: false,
    website_url: '',
    instagram: '',
    facebook: '',
    logo_url: '',
    bank_name: '',
    bank_account_name: '',
    bank_account_bsb: '',
    bank_account_number: '',
    payid: '',
    service_area: '',
    is_australian_business: true,
  });

  // Load config and local settings on mount
  useEffect(() => {
    const stored = localStorage.getItem('globalSettings');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setLocalSettings({
          invoice_due_days: parsed.invoice_due_days ?? 14,
          low_stock_threshold: parsed.low_stock_threshold ?? 5,
          sos_primary_number: parsed.sos_primary_number ?? '000',
          business_hours_start: parsed.business_hours_start ?? '08:00',
          business_hours_end: parsed.business_hours_end ?? '18:00',
        });
      } catch (e) {
        console.error('Failed to parse local settings:', e);
      }
    }
  }, []);

  // Populate form when config loads
  useEffect(() => {
    if (config) {
      console.log('GlobalSettingsPage: Config loaded', config);
      setFormData({
        biz_name: config.biz_name || '',
        biz_type: config.biz_type || 'detailing',
        biz_mode: config.biz_mode || 'fixed',
        owner_name: config.owner_name || '',
        contact_email: config.contact_email || '',
        contact_phone: config.contact_phone || '',
        abn: config.abn || '',
        gst_registered: config.gst_registered || false,
        website_url: config.website_url || '',
        instagram: config.instagram || '',
        facebook: config.facebook || '',
        logo_url: config.logo_url || '',
        bank_name: config.bank_name || '',
        bank_account_name: config.bank_account_name || '',
        bank_account_bsb: config.bank_account_bsb || '',
        bank_account_number: config.bank_account_number || '',
        payid: config.payid || '',
        service_area: config.service_area || '',
        is_australian_business: config.is_australian_business ?? true,
      });
    }
  }, [config]);

  const handleLocalSettingChange = (field: keyof LocalSettings, value: string | number | boolean) => {
    setLocalSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Save local settings to localStorage
      localStorage.setItem('globalSettings', JSON.stringify(localSettings));

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        setLoading(false);
        return;
      }

      console.log('GlobalSettingsPage: Saving config...', {
        configId: config?.id,
        biz_type: formData.biz_type,
        biz_mode: formData.biz_mode,
      });

      // Check if config exists
      if (config?.id) {
        // UPDATE - don't include user_id in the payload
        const { data: updateData, error: updateError } = await supabase
          .from('business_config')
          .update({
            biz_name: formData.biz_name,
            biz_type: formData.biz_type,
            biz_mode: formData.biz_mode,
            owner_name: formData.owner_name,
            contact_email: formData.contact_email,
            contact_phone: formData.contact_phone,
            abn: formData.abn || null,
            gst_registered: formData.gst_registered,
            website_url: formData.website_url || null,
            instagram: formData.instagram || null,
            facebook: formData.facebook || null,
            logo_url: formData.logo_url || null,
            bank_name: formData.bank_name || null,
            bank_account_name: formData.bank_account_name || null,
            bank_account_bsb: formData.bank_account_bsb || null,
            bank_account_number: formData.bank_account_number || null,
            payid: formData.payid || null,
            service_area: formData.service_area || null,
            is_australian_business: formData.is_australian_business,
            onboarding_completed: true,
          })
          .eq('id', config.id)
          .select();

        if (updateError) {
          console.error('GlobalSettingsPage: Update error:', updateError);
          throw updateError;
        }

        console.log('GlobalSettingsPage: Update successful:', updateData);
      } else {
        // INSERT - include user_id
        const { data: insertData, error: insertError } = await supabase
          .from('business_config')
          .insert([{
            user_id: user.id,
            biz_name: formData.biz_name,
            biz_type: formData.biz_type,
            biz_mode: formData.biz_mode,
            owner_name: formData.owner_name,
            contact_email: formData.contact_email,
            contact_phone: formData.contact_phone,
            abn: formData.abn || null,
            gst_registered: formData.gst_registered,
            website_url: formData.website_url || null,
            instagram: formData.instagram || null,
            facebook: formData.facebook || null,
            logo_url: formData.logo_url || null,
            bank_name: formData.bank_name || null,
            bank_account_name: formData.bank_account_name || null,
            bank_account_bsb: formData.bank_account_bsb || null,
            bank_account_number: formData.bank_account_number || null,
            payid: formData.payid || null,
            service_area: formData.service_area || null,
            is_australian_business: formData.is_australian_business,
            onboarding_completed: true,
          }])
          .select();

        if (insertError) {
          console.error('GlobalSettingsPage: Insert error:', insertError);
          throw insertError;
        }

        console.log('GlobalSettingsPage: Insert successful:', insertData);
      }

      toast.success('Business settings saved successfully!');

      // Refresh config to propagate changes to all components
      if (refreshConfig) {
        refreshConfig();
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save settings: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full h-12 bg-white rounded-xl border-slate-200 font-bold focus:ring-2 ring-purple-500 outline-none";
  const labelClass = "text-[10px] font-black text-slate-500 uppercase mb-1 block";
  const sectionClass = "space-y-4";

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-2 mb-4">
        <Settings className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-black uppercase">Business Configuration</h2>
      </div>

      {configLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 font-bold">Loading settings...</p>
        </div>
      ) : (
        <>
          {/* Section 1: About You */}
          <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-5 h-5 text-purple-600" />
              <h3 className="font-black uppercase text-slate-900">About You</h3>
            </div>
            <div className={sectionClass}>
              <div>
                <label className={labelClass}>Your Full Name</label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Jane Smith"
                />
              </div>
              <div>
                <label className={labelClass}>Business Email Address</label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. hello@yourbusiness.com"
                />
              </div>
              <div>
                <label className={labelClass}>Mobile Phone Number</label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. 0412 345 678"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Your Business */}
          <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              <h3 className="font-black uppercase text-slate-900">Your Business</h3>
            </div>
            <div className={sectionClass}>
              <div>
                <label className={labelClass}>Business / Trading Name</label>
                <Input
                  value={formData.biz_name}
                  onChange={(e) => setFormData({ ...formData, biz_name: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Your Business Name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Industry</label>
                  <select
                    value={formData.biz_type}
                    onChange={(e) => setFormData({ ...formData, biz_type: e.target.value })}
                    className={inputClass}
                  >
                    <option value="detailing">Car Detailing</option>
                    <option value="painting">Painting</option>
                    <option value="handyman">Handyman</option>
                    <option value="mowing">Lawn Care</option>
                    <option value="cleaning">Cleaning Services</option>
                    <option value="support_worker">Support Worker</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Pricing Style</label>
                  <select
                    value={formData.biz_mode}
                    onChange={(e) => setFormData({ ...formData, biz_mode: e.target.value })}
                    className={inputClass}
                  >
                    <option value="fixed">Fixed Price</option>
                    <option value="quote">Quote Based</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Service Area</label>
                <Input
                  value={formData.service_area}
                  onChange={(e) => setFormData({ ...formData, service_area: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Brisbane Northside"
                />
              </div>
              <label className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-purple-500 rounded"
                  checked={formData.is_australian_business}
                  onChange={(e) => setFormData({ ...formData, is_australian_business: e.target.checked })}
                />
                <div>
                  <p className="text-slate-900 font-bold text-sm">🇦🇺 Proudly Australian Business</p>
                  <p className="text-slate-500 text-xs">Tick this if your business is based in Australia.</p>
                </div>
              </label>
            </div>
          </div>

          {/* Section 3: Tax & Compliance */}
          <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-5 h-5 text-purple-600" />
              <h3 className="font-black uppercase text-slate-900">Tax & Compliance</h3>
            </div>
            <div className={sectionClass}>
              <div>
                <label className={labelClass}>
                  {formData.is_australian_business ? 'ABN (Australian Business Number)' : 'Business / Tax ID Number'}
                </label>
                <Input
                  value={formData.abn}
                  onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                  className={inputClass}
                  placeholder={formData.is_australian_business ? 'e.g. 12 345 678 901' : 'e.g. Your local tax ID'}
                />
              </div>
              <label className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-purple-500 rounded"
                  checked={formData.gst_registered}
                  onChange={(e) => setFormData({ ...formData, gst_registered: e.target.checked })}
                />
                <div>
                  <p className="text-slate-900 font-bold text-sm">
                    {formData.is_australian_business ? 'Registered for GST' : 'Registered for GST / VAT / Sales Tax'}
                  </p>
                  <p className="text-slate-500 text-xs">
                    {formData.is_australian_business
                      ? 'Tick if you are registered for GST. Tax will be shown on invoices.'
                      : 'Tick if you collect consumption tax. This will appear on your invoices.'}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Section 4: Online Presence */}
          <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-5 h-5 text-purple-600" />
              <h3 className="font-black uppercase text-slate-900">Online Presence</h3>
            </div>
            <div className={sectionClass}>
              <div>
                <label className={labelClass}>Website</label>
                <Input
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. https://yourbusiness.com"
                />
              </div>
              <div>
                <label className={labelClass}>Instagram Handle</label>
                <Input
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. @yourbusiness"
                />
              </div>
              <div>
                <label className={labelClass}>Facebook Page</label>
                <Input
                  value={formData.facebook}
                  onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. facebook.com/yourbusiness"
                />
              </div>
              <div>
                <label className={labelClass}>Logo URL</label>
                <Input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. https://yourdomain.com/logo.png"
                />
                <p className="text-[9px] text-slate-500 font-bold uppercase mt-1">
                  Paste a direct link to your logo image. It will appear on quotes and invoices.
                </p>
              </div>
            </div>
          </div>

          {/* Section 5: Payment Details */}
          <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-5 h-5 text-purple-600" />
              <h3 className="font-black uppercase text-slate-900">Payment Details</h3>
            </div>
            <div className={sectionClass}>
              <div>
                <label className={labelClass}>Account Name</label>
                <Input
                  value={formData.bank_account_name}
                  onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Your Name or Business Name"
                />
              </div>
              <div>
                <label className={labelClass}>Bank Name</label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Commonwealth Bank"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    {formData.is_australian_business ? 'BSB' : 'Sort Code / Routing No.'}
                  </label>
                  <Input
                    value={formData.bank_account_bsb}
                    onChange={(e) => setFormData({ ...formData, bank_account_bsb: e.target.value })}
                    className={inputClass}
                    placeholder={formData.is_australian_business ? 'e.g. 062-000' : 'e.g. 04-00-04'}
                  />
                </div>
                <div>
                  <label className={labelClass}>Account Number</label>
                  <Input
                    value={formData.bank_account_number}
                    onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                    className={inputClass}
                    placeholder="e.g. 12345678"
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>
                  {formData.is_australian_business ? 'PayID (Optional)' : 'Payment Link / Reference (Optional)'}
                </label>
                <Input
                  value={formData.payid}
                  onChange={(e) => setFormData({ ...formData, payid: e.target.value })}
                  className={inputClass}
                  placeholder={formData.is_australian_business ? 'e.g. hello@yourbusiness.com' : 'e.g. paypal.me/yourbusiness'}
                />
              </div>
            </div>
          </div>

          {/* Section 6: Operational Settings (localStorage) */}
          <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-purple-600" />
              <h3 className="font-black uppercase text-slate-900">Operational Settings</h3>
            </div>
            <div className={sectionClass}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Invoice Due Days</label>
                  <Input
                    type="number"
                    value={localSettings.invoice_due_days}
                    onChange={(e) => handleLocalSettingChange('invoice_due_days', parseInt(e.target.value) || 14)}
                    className={inputClass}
                  />
                  <p className="text-[9px] text-slate-500 font-bold uppercase mt-1">Invoices are due X days after issue</p>
                </div>
                <div>
                  <label className={labelClass}>Low Stock Threshold</label>
                  <Input
                    type="number"
                    value={localSettings.low_stock_threshold}
                    onChange={(e) => handleLocalSettingChange('low_stock_threshold', parseInt(e.target.value) || 5)}
                    className={inputClass}
                  />
                  <p className="text-[9px] text-slate-500 font-bold uppercase mt-1">Alert when stock falls below this level</p>
                </div>
              </div>
              <div>
                <label className={labelClass}>SOS Emergency Number</label>
                <Input
                  value={localSettings.sos_primary_number}
                  onChange={(e) => handleLocalSettingChange('sos_primary_number', e.target.value)}
                  className={inputClass}
                  placeholder="000 (AU), 911 (US), 999 (UK), 112 (EU)"
                />
                <p className="text-[9px] text-slate-500 font-bold uppercase mt-1">Default emergency number for your region</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Business Hours Start</label>
                  <Input
                    type="time"
                    value={localSettings.business_hours_start}
                    onChange={(e) => handleLocalSettingChange('business_hours_start', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Business Hours End</label>
                  <Input
                    type="time"
                    value={localSettings.business_hours_end}
                    onChange={(e) => handleLocalSettingChange('business_hours_end', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={loading}
            className="w-full h-14 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-purple-500/10"
          >
            {loading ? 'Saving...' : '💾 Save All Settings'}
          </Button>
        </>
      )}
    </div>
  );
}
